import apiClient from './api-client';
import { DataElement } from './dataelements-service';
import { BaseLabel, LabelType } from './labels-service';
import { apiKeysService } from './apiKeys-service';
import axios from 'axios';
import { config as appConfig } from '../config/config';

// API Path Constants
const API_BASE = '/collectionpoints';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    BY_IDENTIFIER: (identifier: string) => `${API_BASE}/identifier/${identifier}`,
    PURPOSES: (id: number) => `${API_BASE}/${id}/purposes`,
    DATA_ELEMENTS: (id: number) => `${API_BASE}/${id}/data-elements`,
    DP_OPTIONS: (id: number) => `${API_BASE}/${id}/dp-options`,
    LABELS: (id: number) => `${API_BASE}/${id}/labels`,
    PUBLISH: (id: number) => `${API_BASE}/${id}/publish`,
    STATUS: (id: number) => `${API_BASE}/${id}/status`,
    CONSENT: (identifier: string) => `${API_BASE}/${identifier}/consent`,
    CONSENT_SANDBOX: (identifier: string) => `${API_BASE}/${identifier}/consent/sandbox`,
    NOTICE_CONFIG: (id: number) => `${API_BASE}/${id}/notice-config`,
    EVENT_PROPAGATION: (id: number) => `${API_BASE}/${id}/event-propagation`,
} as const;

export type EventPropagationEventType = 'CONSENT_RECEIVED' | 'CONSENT_WITHDRAWN' | 'RETENTION_EXPIRED' | 'ERASURE_REQUESTED';
export interface EventPropagationTarget {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    active?: boolean;
}
export interface EventPropagationItem {
    eventType: EventPropagationEventType;
    targets: EventPropagationTarget[];
}

export interface CollectionPoint {
  CollectionPointId: number;
  ClientId: number;
  DepartmentId: number;
  DepartmentName?: string;
  Name: string;
  Description?: string;
  CollectionPointType: 'API' | 'HOSTED_WEBFORM' | 'EMBEDDED_WEBFORM' | 'NOTICE' | 'BULK_UPLOAD';
  CollectionPointIdentifier: string;
  IsActive: boolean;
  IsPublished: boolean;
  IdentifierDataElementId: number;
  IdentifierElementName?: string;
  IdentifierElementDescription?: string;
  IdentifierElementCode?: string;
  IdentifierElementTypeName?: string;
  TrackGeolocation: boolean;
  PrivacyNoticeUrl?: string;
  TOSUrl?: string;
  EnableCollectionPointEvents: boolean;
  EnableParentChildRelationship: boolean;
  ParentIdentifierDataElementId?: number;
  EnableDoubleOptIn: boolean;
  CreatedBy: number;
  CreatedDate: string;
  ModifiedDate?: string;
  ModifiedBy?: number;
  // Notice Designer configuration
  NoticeConfigJSON?: string | null;
}

export interface CollectionPointQueryParams {
    filters?: {
        isPublished?: boolean;
        trackGeolocation?: boolean;
        type?: 'API' | 'HOSTED_WEBFORM' | 'EMBEDDED_WEBFORM' | 'NOTICE' | 'BULK_UPLOAD';
    };
    pagination: {
        page: number;
        pageSize: number;
    };
    sortBy: 'A-Z' | 'Z-A' | 'newest' | 'oldest' | 'Newest' | 'Oldest' | 'a-z' | 'z-a';
}

export interface CollectionPointsPaginatedResponse {
    success: boolean;
    data: CollectionPoint[];
    pagination: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}

export interface CollectionPointPurposeMap {
  CollectionPointId: number;
  MappingId: number;
  PurposeId: number;
  LawfulGroundId: number;
  PurposeCategoryId: number;
  IsRequired: boolean;
  RetentionPeriodTypeId: number;
  TimeBasedPeriod?: number;
  TimeBasedUnitId?: number;
  StartDateTypeId: number;
  CustomStartDate?: Date;
  ExtensionAllowed: boolean;
  ExtensionReason?: string;
  EndOnPurposeFulfilled: boolean;
  EndOnConsentWithdrawn: boolean;
  EndOnRetentionExpired: boolean;
  EndOnErasureRequested: boolean;
  EndOnAccountClosure: boolean;
  LegalRetentionRequired: boolean;
  LegalRetentionPeriod?: number;
  CreatedBy: number;
  CreatedDate: string;
  ModifiedDate?: string;
  ModifiedBy?: number;
  IsActive: boolean;
  // Enhanced fields from server
  purposeName: string;
  purposeDescription?: string;
  lawfulGroundLabel: string;
  purposeCategoryLabel: string;
  retentionPeriodTypeLabel: string;
  timeBasedUnitLabel?: string;
  startDateTypeLabel: string;
  dataElements: {
    dataElementId: number;
    elementName: string;
    elementDescription?: string;
    elementCode: string;
  }[];
  processors: {
    processorId: number;
    processorName: string;
    processorDescription?: string;
    processorType: string;
    processorWebsite?: string;
  }[];
  preferences: {
    preferenceId: number;
    preferenceName: string;
    preferenceDescription?: string;
    preferenceType: string;
  }[];
}

export interface CollectionPointDataElementMap {
  DataElementId: number;
  IsRequired: boolean;
  DisplayOrder: number;
  elementName: string;
  elementDescription?: string;
  dataType: string;
  validationRules?: string;
}

export interface CollectionPointDPOptionMap {
  CollectionPointId: number;
  MappingId: number;
  DPOptionId: number;
  Name: string;
  Description: string;
  DisplayAs: 'button' | 'checkbox';
  AllowMultipleSelection: boolean;
  IsMandatory: boolean;
  Options: {
    value: string;
    displayOrder: number;
  }[];
  Language: string;
  VersionNumber: number;
  IsPublished: boolean;
  PublishedDate: string | null;
  PublishedBy: number | null;
  CreatedBy: number;
  CreatedDate: string;
  ModifiedDate: string | null;
  ModifiedBy: number | null;
  IsActive: boolean;
}

export interface CollectionPointLabelMap {
  CollectionPointId: number;
  MappingId: number;
  LabelId: number;
  LabelKey: string;
  LabelType: LabelType;
  Category: string;
  LabelText: string;
  Description?: string;
  Language: string;
  VersionNumber: number;
  IsPublished: boolean;
  PublishedDate: string | null;
  PublishedBy: number | null;
  CreatedBy: number;
  CreatedDate: string;
  ModifiedDate: string | null;
  ModifiedBy: number | null;
  IsActive: boolean;
}

// Interface for collection point purpose constants
export interface CollectionPointPurposeConstant {
  ConstantId: number;
  Category: 'LAWFUL_GROUND' | 'RETENTION_TYPE' | 'TIME_UNIT' | 'START_DATE_TYPE' | 'PURPOSE_CATEGORY' | 'COLLECTION_POINT_TYPE';
  Code: string;
  Label: string;
  Description?: string;
  DisplayOrder: number;
  IsActive: boolean;
  CreatedBy: number;
  CreatedDate: string;
  ModifiedDate?: string;
  ModifiedBy?: number;
}

// Snapshot interfaces for Phase 1A
export interface SnapshotTranslation {
  languageCode: string;
  translatedName: string;
  translatedDescription?: string;
}

export interface SnapshotPurpose {
  id: number;
  key: string;
  name: string;
  description?: string;
  isRequired: boolean;
  lawfulGround: {
    id: number;
    code: string;
    label: string;
  };
  purposeCategory: {
    id: number;
    code: string;
    label: string;
  };
  retention: {
    type: {
      id: number;
      code: string;
      label: string;
    };
    timeBasedPeriod?: number;
    timeBasedUnit?: {
      id: number;
      code: string;
      label: string;
    };
    startDateType: {
      id: number;
      code: string;
      label: string;
    };
    customStartDate?: Date;
    extensionAllowed: boolean;
    extensionReason?: string;
    endConditions: {
      onPurposeFulfilled: boolean;
      onConsentWithdrawn: boolean;
      onRetentionExpired: boolean;
      onErasureRequested: boolean;
      onAccountClosure: boolean;
    };
    legalRetentionRequired: boolean;
    legalRetentionPeriod?: number;
  };
  processors: SnapshotProcessor[];
  preferences: SnapshotPurposePreference[];
  translations: SnapshotTranslation[];
}

export interface SnapshotDataElement {
  id: number;
  key: string;
  name: string;
  description?: string;
  code: string;
  dataType: string;
  isRequired: boolean;
  validationRules?: string;
  translations: SnapshotTranslation[];
}

export interface SnapshotPreference {
  id: number;
  key: string;
  name: string;
  description?: string;
  type: string;
  isRequired: boolean;
  displayOrder: number;
  allowMultipleSelection: boolean;
  options: SnapshotPreferenceOption[];
  translations: SnapshotTranslation[];
}

export interface SnapshotPreferenceOption {
  id: number;
  key: string;
  name: string;
  description?: string;
  displayOrder: number;
  isDefaultSelected: boolean;
  translations: SnapshotTranslation[];
}

export interface SnapshotLabel {
  id: number;
  key: string;
  name: string;
  description?: string;
  type: string;
  category: string;
  text: string;
  translations: SnapshotTranslation[];
}

export interface SnapshotProcessor {
  id: number;
  key: string;
  name: string;
  description?: string;
  type: string;
  website?: string;
  translations: SnapshotTranslation[];
}

export interface SnapshotPurposePreference {
  id: number;
  key: string;
  name: string;
  description?: string;
  type: string;
  isRequired: boolean;
  displayOrder: number;
  allowMultipleSelection: boolean;
  options: SnapshotPreferenceOption[];
  translations: SnapshotTranslation[];
}

export interface SnapshotStructure {
  collectionPoint: {
    id: number;
    identifier: string;
    name: string;
    description?: string;
    type: string;
    trackGeolocation: boolean;
    publishedDate: Date;
    publishedBy: number;
  };
  purposes: SnapshotPurpose[];
  dataElements: SnapshotDataElement[];
  preferences: SnapshotPreference[];
  labels: SnapshotLabel[];
  metadata: {
    version: string;
    generatedAt: Date;
    generatedBy: number;
  };
}

// Mock data for data element mappings
const mockCollectionPointDataElements: Record<number, CollectionPointDataElementMap[]> = {
  1: [
    {
      DataElementId: 1,
      IsRequired: true,
      DisplayOrder: 1,
      elementName: 'Full Name',
      elementDescription: 'Legal full name of the individual',
      dataType: 'Text',
      validationRules: 'required'
    },
    {
      DataElementId: 4,
      IsRequired: true,
      DisplayOrder: 2,
      elementName: 'Email Address',
      elementDescription: 'Primary email address for communication',
      dataType: 'Email',
      validationRules: 'required|email'
    }
  ],
  2: [
    {
      DataElementId: 5,
      IsRequired: true,
      DisplayOrder: 1,
      elementName: 'Contact Numbers',
      elementDescription: 'Contact phone numbers',
      dataType: 'Phone',
      validationRules: 'required'
    }
  ]
};

// Mock data for DP Option mappings
const mockCollectionPointDPOptions: Record<number, CollectionPointDPOptionMap[]> = {
  1: [
    {
      CollectionPointId: 1,
      MappingId: 1,
      DPOptionId: 1,
      Name: 'Data Processing Option 1',
      Description: 'This is a sample description for option 1',
      DisplayAs: 'button',
      AllowMultipleSelection: false,
      IsMandatory: true,
      Options: [
        { value: 'Yes', displayOrder: 0 },
        { value: 'No', displayOrder: 1 },
        { value: 'Maybe', displayOrder: 2 }
      ],
      Language: 'en',
      VersionNumber: 1,
      IsPublished: true,
      PublishedDate: '2024-03-15T10:00:00Z',
      PublishedBy: 1001,
      CreatedBy: 1001,
      CreatedDate: '2024-03-15T09:00:00Z',
      ModifiedDate: '2024-03-15T09:30:00Z',
      ModifiedBy: 1001,
      IsActive: true
    },
    {
      CollectionPointId: 1,
      MappingId: 2,
      DPOptionId: 2,
      Name: 'Data Processing Option 2',
      Description: 'This is a sample description for option 2',
      DisplayAs: 'checkbox',
      AllowMultipleSelection: true,
      IsMandatory: false,
      Options: [
        { value: 'Email', displayOrder: 0 },
        { value: 'SMS', displayOrder: 1 },
        { value: 'Phone', displayOrder: 2 }
      ],
      Language: 'en',
      VersionNumber: 1,
      IsPublished: true,
      PublishedDate: '2024-03-15T10:00:00Z',
      PublishedBy: 1001,
      CreatedBy: 1001,
      CreatedDate: '2024-03-15T09:00:00Z',
      ModifiedDate: '2024-03-15T09:30:00Z',
      ModifiedBy: 1001,
      IsActive: true
    }
  ],
  2: [
    {
      CollectionPointId: 2,
      MappingId: 3,
      DPOptionId: 3,
      Name: 'Data Processing Option 3',
      Description: 'This is a sample description for option 3',
      DisplayAs: 'button',
      AllowMultipleSelection: false,
      IsMandatory: true,
      Options: [
        { value: 'Accept', displayOrder: 0 },
        { value: 'Decline', displayOrder: 1 }
      ],
      Language: 'en',
      VersionNumber: 1,
      IsPublished: true,
      PublishedDate: '2024-03-14T15:00:00Z',
      PublishedBy: 1001,
      CreatedBy: 1001,
      CreatedDate: '2024-03-14T14:00:00Z',
      ModifiedDate: null,
      ModifiedBy: null,
      IsActive: true
    }
  ]
};

// Mock data for label mappings
const mockCollectionPointLabels: Record<number, CollectionPointLabelMap[]> = {
  1: [
    {
      CollectionPointId: 1,
      MappingId: 1,
      LabelId: 1,
      LabelKey: 'consent_form_title',
      LabelType: 'TITLE',
      Category: 'FORM',
      LabelText: 'Consent Collection Form',
      Description: 'Main title for the consent collection form',
      Language: 'en',
      VersionNumber: 1,
      IsPublished: true,
      PublishedDate: '2024-03-15T10:00:00Z',
      PublishedBy: 1001,
      CreatedBy: 1001,
      CreatedDate: '2024-03-15T09:00:00Z',
      ModifiedDate: '2024-03-15T09:30:00Z',
      ModifiedBy: 1001,
      IsActive: true
    },
    {
      CollectionPointId: 1,
      MappingId: 2,
      LabelId: 2,
      LabelKey: 'submit_button',
      LabelType: 'BUTTON',
      Category: 'ACTION',
      LabelText: 'Submit',
      Description: 'Submit button text for forms',
      Language: 'en',
      VersionNumber: 1,
      IsPublished: true,
      PublishedDate: '2024-03-15T10:00:00Z',
      PublishedBy: 1001,
      CreatedBy: 1001,
      CreatedDate: '2024-03-15T09:00:00Z',
      ModifiedDate: '2024-03-15T09:30:00Z',
      ModifiedBy: 1001,
      IsActive: true
    },
    {
      CollectionPointId: 1,
      MappingId: 3,
      LabelId: 3,
      LabelKey: 'privacy_notice_title',
      LabelType: 'TITLE',
      Category: 'LEGAL',
      LabelText: 'Privacy Notice',
      Description: 'Title for privacy notice section',
      Language: 'en',
      VersionNumber: 1,
      IsPublished: true,
      PublishedDate: '2024-03-15T10:00:00Z',
      PublishedBy: 1001,
      CreatedBy: 1001,
      CreatedDate: '2024-03-15T09:00:00Z',
      ModifiedDate: '2024-03-15T09:30:00Z',
      ModifiedBy: 1001,
      IsActive: true
    }
  ],
  2: [
    {
      CollectionPointId: 2,
      MappingId: 4,
      LabelId: 4,
      LabelKey: 'email_confirmation_subject',
      LabelType: 'TITLE',
      Category: 'EMAIL',
      LabelText: 'Confirm Your Consent',
      Description: 'Subject line for consent confirmation emails',
      Language: 'en',
      VersionNumber: 1,
      IsPublished: true,
      PublishedDate: '2024-03-14T15:00:00Z',
      PublishedBy: 1001,
      CreatedBy: 1001,
      CreatedDate: '2024-03-14T14:00:00Z',
      ModifiedDate: null,
      ModifiedBy: null,
      IsActive: true
    },
    {
      CollectionPointId: 2,
      MappingId: 5,
      LabelId: 5,
      LabelKey: 'required_field_error',
      LabelType: 'ERROR',
      Category: 'VALIDATION',
      LabelText: 'This field is required',
      Description: 'Error message for required fields',
      Language: 'en',
      VersionNumber: 1,
      IsPublished: true,
      PublishedDate: '2024-03-14T15:00:00Z',
      PublishedBy: 1001,
      CreatedBy: 1001,
      CreatedDate: '2024-03-14T14:00:00Z',
      ModifiedDate: null,
      ModifiedBy: null,
      IsActive: true
    }
  ]
};

// Mock data
const mockCollectionPoints: CollectionPoint[] = [
  {
    CollectionPointId: 1,
    ClientId: 1,
    DepartmentId: 1,
    Name: 'Main Website Consent Form',
    Description: 'Primary consent collection point for website visitors',
    CollectionPointType: 'HOSTED_WEBFORM',
    CollectionPointIdentifier: 'main-website-consent-2024',
    IsActive: true,
    IsPublished: true,
    IdentifierDataElementId: 1,
    TrackGeolocation: true,
    PrivacyNoticeUrl: 'https://example.com/privacy',
    TOSUrl: 'https://example.com/tos',
    EnableCollectionPointEvents: true,
    EnableParentChildRelationship: false,
    ParentIdentifierDataElementId: undefined,
    EnableDoubleOptIn: true,
    CreatedBy: 1,
    CreatedDate: '2024-01-01T00:00:00Z',
    ModifiedDate: '2024-01-02T00:00:00Z',
    ModifiedBy: 1
  },
  {
    CollectionPointId: 2,
    ClientId: 1,
    DepartmentId: 2,
    Name: 'API Consent Endpoint',
    Description: 'API endpoint for programmatic consent collection',
    CollectionPointType: 'API',
    CollectionPointIdentifier: 'api-consent-v1',
    IsActive: true,
    IsPublished: true,
    IdentifierDataElementId: 2,
    TrackGeolocation: false,
    PrivacyNoticeUrl: 'https://api.example.com/privacy',
    TOSUrl: 'https://api.example.com/tos',
    EnableCollectionPointEvents: true,
    EnableParentChildRelationship: true,
    ParentIdentifierDataElementId: 3,
    EnableDoubleOptIn: false,
    CreatedBy: 1,
    CreatedDate: '2024-01-03T00:00:00Z',
    ModifiedDate: '2024-01-04T00:00:00Z',
    ModifiedBy: 1
  },
  {
    CollectionPointId: 3,
    ClientId: 1,
    DepartmentId: 1,
    Name: 'Bulk Upload Template',
    Description: 'Template for bulk consent uploads',
    CollectionPointType: 'BULK_UPLOAD',
    CollectionPointIdentifier: 'bulk-upload-template-2024',
    IsActive: true,
    IsPublished: false,
    IdentifierDataElementId: 4,
    TrackGeolocation: false,
    PrivacyNoticeUrl: 'https://example.com/bulk-upload-privacy',
    TOSUrl: 'https://example.com/bulk-upload-tos',
    EnableCollectionPointEvents: false,
    EnableParentChildRelationship: false,
    ParentIdentifierDataElementId: undefined,
    EnableDoubleOptIn: false,
    CreatedBy: 1,
    CreatedDate: '2024-01-05T00:00:00Z'
  }
];

// Mock API functions
export const collectionPointsService = {
    getCollectionPoints: async (queryParams?: CollectionPointQueryParams): Promise<CollectionPointsPaginatedResponse> => {
        const params = new URLSearchParams();
        
        if (queryParams?.filters) {
            if (queryParams.filters.isPublished !== undefined) {
                params.append('isPublished', queryParams.filters.isPublished.toString());
            }
            if (queryParams.filters.trackGeolocation !== undefined) {
                params.append('trackGeolocation', queryParams.filters.trackGeolocation.toString());
            }
            if (queryParams.filters.type) {
                params.append('type', queryParams.filters.type);
            }
        }

        if (queryParams?.pagination) {
            params.append('page', queryParams.pagination.page.toString());
            params.append('pageSize', queryParams.pagination.pageSize.toString());
        }

        if (queryParams?.sortBy) {
            params.append('sortBy', queryParams.sortBy);
        }

        const response = await apiClient.get<CollectionPointsPaginatedResponse>(`${API_PATHS.BASE}?${params.toString()}`);
        return response;
    },

  getCollectionPointById: async (id: number): Promise<CollectionPoint> => {
    const response = await apiClient.get<{ success: boolean; data: CollectionPoint }>(API_PATHS.BY_ID(id));
    return response.data;
  },

  createCollectionPoint: async (collectionPoint: {
    DepartmentId: number;
    Name: string;
    Description?: string;
    CollectionPointType: 'API' | 'HOSTED_WEBFORM' | 'EMBEDDED_WEBFORM' | 'NOTICE' | 'BULK_UPLOAD';
    IdentifierDataElementId: number;
    TrackGeolocation?: boolean;
  }): Promise<CollectionPoint> => {
    const response = await apiClient.post<{ success: boolean; data: CollectionPoint }>(
      API_PATHS.BASE,
      collectionPoint
    );
    return response.data;
  },

  updateCollectionPoint: async (id: number, updates: Partial<CollectionPoint>): Promise<CollectionPoint> => {
    const response = await apiClient.put<{ success: boolean; data: CollectionPoint }>(
      `/collectionpoints/${id}`,
      updates
    );
    return response.data;
  },

  deleteCollectionPoint: async (id: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockCollectionPoints.findIndex(cp => cp.CollectionPointId === id);
        if (index === -1) {
          reject(new Error('Collection Point not found'));
          return;
        }

        const collectionPoint = mockCollectionPoints[index];
        
        if (!collectionPoint.IsPublished) {
          // Physical delete for draft mode
          mockCollectionPoints.splice(index, 1);
        } else {
          // Soft delete for published collection points
          mockCollectionPoints[index] = {
            ...collectionPoint,
            IsActive: false,
            ModifiedDate: new Date().toISOString()
          };
        }
        resolve();
      }, 1000);
    });
  },

  publishCollectionPoint: async (id: number): Promise<CollectionPoint> => {
    try {
      // Use direct axios call to bypass retry logic and prevent race conditions
      const token = localStorage.getItem('auth_token');
      
      const response = await axios.post(
        `${appConfig.api.baseUrl}${API_PATHS.PUBLISH(id)}`,
        {},
        {
          timeout: 60000, // 60 seconds timeout for publish operation
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );
      
      if (!response.data.success) {
        throw new Error('Failed to publish collection point');
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error publishing collection point:', error);
      throw error;
    }
  },

  updateCollectionPointStatus: async (id: number, IsActive: boolean): Promise<CollectionPoint> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockCollectionPoints.findIndex(cp => cp.CollectionPointId === id);
        if (index === -1) {
          reject(new Error('Collection Point not found'));
          return;
        }
        const updatedCollectionPoint = {
          ...mockCollectionPoints[index],
          IsActive,
          ModifiedDate: new Date().toISOString()
        };
        mockCollectionPoints[index] = updatedCollectionPoint;
        resolve(updatedCollectionPoint);
      }, 1000);
    });
  },

  undeleteCollectionPoint: async (id: number): Promise<CollectionPoint> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockCollectionPoints.findIndex(cp => cp.CollectionPointId === id);
        if (index === -1) {
          reject(new Error('Collection Point not found'));
          return;
        }

        const collectionPoint = mockCollectionPoints[index];
        
        // Only allow undelete if the collection point was soft-deleted (isActive = false)
        if (collectionPoint.IsActive) {
          reject(new Error('Collection Point is already active'));
          return;
        }

        const updatedCollectionPoint = {
          ...collectionPoint,
          IsActive: true,
          ModifiedDate: new Date().toISOString()
        };
        mockCollectionPoints[index] = updatedCollectionPoint;
        resolve(updatedCollectionPoint);
      }, 1000);
    });
  },

  getMappedPurposes: async (collectionPointId: number): Promise<CollectionPointPurposeMap[]> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: any[] }>(
        API_PATHS.PURPOSES(collectionPointId)
      );
      
      if (!response.success) {
        throw new Error('Failed to fetch mapped purposes');
      }

      // Map the API response to the expected interface with enhanced fields
      return response.data.map(purpose => ({
        CollectionPointId: purpose.CollectionPointId || collectionPointId,
        MappingId: purpose.MappingId || 0,
        PurposeId: purpose.PurposeId,
        LawfulGroundId: purpose.LawfulGroundId,
        PurposeCategoryId: purpose.PurposeCategoryId,
        IsRequired: purpose.IsRequired,
        RetentionPeriodTypeId: purpose.RetentionPeriodTypeId,
        TimeBasedPeriod: purpose.TimeBasedPeriod,
        TimeBasedUnitId: purpose.TimeBasedUnitId,
        StartDateTypeId: purpose.StartDateTypeId,
        CustomStartDate: purpose.CustomStartDate,
        ExtensionAllowed: purpose.ExtensionAllowed,
        ExtensionReason: purpose.ExtensionReason,
        EndOnPurposeFulfilled: purpose.EndOnPurposeFulfilled,
        EndOnConsentWithdrawn: purpose.EndOnConsentWithdrawn,
        EndOnRetentionExpired: purpose.EndOnRetentionExpired,
        EndOnErasureRequested: purpose.EndOnErasureRequested,
        EndOnAccountClosure: purpose.EndOnAccountClosure,
        LegalRetentionRequired: purpose.LegalRetentionRequired,
        LegalRetentionPeriod: purpose.LegalRetentionPeriod,
        CreatedBy: purpose.CreatedBy || 0,
        CreatedDate: purpose.CreatedDate || new Date().toISOString(),
        ModifiedDate: purpose.ModifiedDate || null,
        ModifiedBy: purpose.ModifiedBy || null,
        IsActive: purpose.IsActive !== undefined ? purpose.IsActive : true,
        // Enhanced fields from server
        purposeName: purpose.purposeName || 'Unknown Purpose',
        purposeDescription: purpose.purposeDescription,
        lawfulGroundLabel: purpose.lawfulGroundLabel || 'Unknown',
        purposeCategoryLabel: purpose.purposeCategoryLabel || 'Unknown',
        retentionPeriodTypeLabel: purpose.retentionPeriodTypeLabel || 'Unknown',
        timeBasedUnitLabel: purpose.timeBasedUnitLabel,
        startDateTypeLabel: purpose.startDateTypeLabel || 'Unknown',
        dataElements: purpose.dataElements || [],
        processors: purpose.processors || [],
        preferences: purpose.preferences || []
      }));
    } catch (error) {
      console.error('Error fetching mapped purposes:', error);
      throw error;
    }
  },

  mapPurpose: async (collectionPointId: number, purpose: {
    purposeId: number;
    lawfulGroundId: number;
    purposeCategoryId: number;
    isRequired: boolean;
    retentionPeriodTypeId: number;
    timeBasedPeriod?: number;
    timeBasedUnitId?: number;
    startDateTypeId: number;
    customStartDate?: Date;
    extensionAllowed: boolean;
    extensionReason?: string;
    endOnPurposeFulfilled: boolean;
    endOnConsentWithdrawn: boolean;
    endOnRetentionExpired: boolean;
    endOnErasureRequested: boolean;
    endOnAccountClosure: boolean; // NEW
    legalRetentionRequired: boolean; // NEW
    legalRetentionPeriod?: number; // NEW
  }): Promise<any> => {
    try {
      const response = await apiClient.post<{
        success: boolean;
        data: {
          PurposeId: number;
          LawfulGroundId: number;
          PurposeCategoryId: number;
          IsRequired: boolean;
          RetentionPeriodTypeId: number;
          TimeBasedPeriod?: number;
          TimeBasedUnitId?: number;
          StartDateTypeId: number;
          CustomStartDate?: Date;
          ExtensionAllowed: boolean;
          ExtensionReason?: string;
          EndOnPurposeFulfilled: boolean;
          EndOnConsentWithdrawn: boolean;
          EndOnRetentionExpired: boolean;
          EndOnErasureRequested: boolean;
        }[];
      }>(API_PATHS.PURPOSES(collectionPointId), purpose);

      if (!response.success) {
        throw new Error('Failed to map purpose to collection point');
      }

      return response.data;
    } catch (error) {
      console.error('Error mapping purpose:', error);
      throw error;
    }
  },

  unmapPurpose: async (collectionPointId: number, purposeId: number): Promise<void> => {
    try {
      await apiClient.delete<{ success: boolean; message: string }>(`${API_PATHS.BY_ID(collectionPointId)}/purposes/${purposeId}`);
    } catch (error) {
      console.error('Error unmapping purpose:', error);
      throw error;
    }
  },

  getMappedDataElements: async (collectionPointId: number): Promise<CollectionPointDataElementMap[]> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: CollectionPointDataElementMap[] }>(
        API_PATHS.DATA_ELEMENTS(collectionPointId)
      );

      if (!response.success) {
        throw new Error('Failed to fetch mapped data elements');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching mapped data elements:', error);
      throw error;
    }
  },

  mapDataElement: async (collectionPointId: number, dataElement: { dataElementId: number; isRequired: boolean }): Promise<CollectionPointDataElementMap> => {
    try {
      const response = await apiClient.post<{ success: boolean; data: CollectionPointDataElementMap }>(
        `${API_PATHS.DATA_ELEMENTS(collectionPointId)}`,
        dataElement
      );

      if (!response.success) {
        throw new Error('Failed to map data element to collection point');
      }

      return response.data;
    } catch (error) {
      console.error('Error mapping data element:', error);
      throw error;
    }
  },

  unmapDataElement: async (collectionPointId: number, dataElementId: number): Promise<void> => {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `${API_PATHS.DATA_ELEMENTS(collectionPointId)}/${dataElementId}`
      );

      if (!response.success) {
        throw new Error('Failed to unmap data element from collection point');
      }
    } catch (error) {
      console.error('Error unmapping data element:', error);
      throw error;
    }
  },

  getMappedDPOptions: async (collectionPointId: number): Promise<CollectionPointDPOptionMap[]> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: any[] }>(
        `${API_PATHS.BY_ID(collectionPointId)}/preferences`
      );

      if (!response.success) {
        throw new Error('Failed to fetch mapped preferences');
      }

      // Map the API response to the expected interface
      return response.data.map(preference => ({
        CollectionPointId: collectionPointId,
        MappingId: preference.purposePreferenceId, // Using preferenceId as mappingId
        DPOptionId: preference.purposePreferenceId,
        Name: preference.preferenceName || 'Unknown Preference',
        Description: preference.preferenceDescription || '',
        DisplayAs: preference.preferenceType === 'CHECKBOX' ? 'checkbox' : 'button',
        AllowMultipleSelection: preference.allowMultipleSelection || false,
        IsMandatory: preference.isRequired || false,
        Options: preference.options?.map((option: any) => ({
          value: option.optionName,
          displayOrder: option.DisplayOrder || 0
        })) || [],
        Language: 'en', // Default language
        VersionNumber: 1, // Default version
        IsPublished: true, // Assuming published since we're fetching from API
        PublishedDate: new Date().toISOString(),
        PublishedBy: 1, // Default value
        CreatedBy: 1, // Default value
        CreatedDate: new Date().toISOString(),
        ModifiedDate: null,
        ModifiedBy: null,
        IsActive: true
      }));
    } catch (error) {
      console.error('Error fetching mapped preferences:', error);
      throw error;
    }
  },

  mapDPOption: async (collectionPointId: number, dpOptionId: number, isRequired = true): Promise<CollectionPointDPOptionMap> => {
    try {
      // Prepare the payload as expected by the server
      const payload = {
        purposePreferenceId: dpOptionId,
        isRequired: isRequired
      };

      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${API_PATHS.BY_ID(collectionPointId)}/preferences`,
        payload
      );

      if (!response.success) {
        throw new Error('Failed to map preference to collection point');
      }

      // Return a basic mapping object since the server doesn't return the full mapping details
      // The UI will refresh the mapped preferences list to get the updated data
      const newMapping: CollectionPointDPOptionMap = {
        CollectionPointId: collectionPointId,
        MappingId: dpOptionId, // Using dpOptionId as mapping ID temporarily
        DPOptionId: dpOptionId,
        Name: 'Preference mapped successfully',
        Description: 'Preference has been mapped to this collection point',
        DisplayAs: 'button',
        AllowMultipleSelection: false,
        IsMandatory: true,
        Options: [],
        Language: 'en',
        VersionNumber: 1,
        IsPublished: true,
        PublishedDate: new Date().toISOString(),
        PublishedBy: 1,
        CreatedBy: 1,
        CreatedDate: new Date().toISOString(),
        ModifiedDate: null,
        ModifiedBy: null,
        IsActive: true
      };

      return newMapping;
    } catch (error) {
      console.error('Error mapping preference:', error);
      throw error;
    }
  },

  unmapDPOption: async (collectionPointId: number, dpOptionId: number): Promise<void> => {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `${API_PATHS.BY_ID(collectionPointId)}/preferences/${dpOptionId}`
      );

      if (!response.success) {
        throw new Error('Failed to unmap preference from collection point');
      }
    } catch (error) {
      console.error('Error unmapping preference:', error);
      throw error;
    }
  },

  getMappedLabels: async (collectionPointId: number): Promise<CollectionPointLabelMap[]> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: CollectionPointLabelMap[] }>(
        API_PATHS.LABELS(collectionPointId)
      );

      if (!response.success) {
        throw new Error('Failed to fetch mapped labels');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching mapped labels:', error);
      throw error;
    }
  },

  mapLabel: async (collectionPointId: number, labelId: number): Promise<CollectionPointLabelMap> => {
    try {
      const response = await apiClient.post<{ success: boolean; data: CollectionPointLabelMap }>(
        `${API_PATHS.LABELS(collectionPointId)}/${labelId}`
      );

      if (!response.success) {
        throw new Error('Failed to map label to collection point');
      }

      return response.data;
    } catch (error) {
      console.error('Error mapping label:', error);
      throw error;
    }
  },

  unmapLabel: async (collectionPointId: number, labelId: number): Promise<void> => {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(
        `${API_PATHS.LABELS(collectionPointId)}/${labelId}`
      );

      if (!response.success) {
        throw new Error('Failed to unmap label from collection point');
      }
    } catch (error) {
      console.error('Error unmapping label:', error);
      throw error;
    }
  },

  getCollectionPointPurposeConstants: async (): Promise<CollectionPointPurposeConstant[]> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: CollectionPointPurposeConstant[] }>(
        '/collectionpoints/constants/purposes'
      );
      
      if (!response.success) {
        throw new Error('Failed to fetch collection point purpose constants');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching collection point purpose constants:', error);
      throw error;
    }
  },

  // Get snapshot JSON for a collection point
  getCollectionPointSnapshot: async (collectionPointIdentifier: string): Promise<SnapshotStructure> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: SnapshotStructure }>(
        `/collectionpoints/${collectionPointIdentifier}/detailed`
      );
      
      if (!response.success) {
        throw new Error('Failed to fetch collection point snapshot');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching collection point snapshot:', error);
      throw error;
    }
  },

  // Submit production consent
  submitConsent: async (collectionPointIdentifier: string, payload: ConsentPayload): Promise<ConsentResponse> => {
    try {
      const response = await apiClient.post<ConsentResponse>(
        API_PATHS.CONSENT(collectionPointIdentifier),
        payload
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to submit consent');
      }

      return response;
    } catch (error) {
      console.error('Error submitting consent:', error);
      throw error;
    }
  },

  // Submit sandbox consent (test data)
  submitSandboxConsent: async (collectionPointIdentifier: string, payload: ConsentPayload): Promise<ConsentResponse> => {
    try {
      // Get API key and print to console
      const apiKeyResponse = await apiKeysService.getMyApiKey();
      console.log('API Key:', apiKeyResponse.data.apiKey);
      
      const response = await apiClient.post<ConsentResponse>(
        API_PATHS.CONSENT_SANDBOX(collectionPointIdentifier),
        payload,
        {
          headers: {
            'x-api-key': apiKeyResponse.data.apiKey
          }
        }
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to submit sandbox consent');
      }

      return response;
    } catch (error) {
      console.error('Error submitting sandbox consent:', error);
      throw error;
    }
  },

  // Update notice configuration for a NOTICE-type collection point
    updateNoticeConfig: async (collectionPointId: number, noticeConfig: {
        titleLabelId: number;
        subtitleLabelId?: number | null;
        descriptionLabelId?: number | null;
        submitButtonLabelId: number;
        dataElementOrder: number[];
        purposeOrder: number[];
    }): Promise<CollectionPoint> => {
    try {
      const response = await apiClient.put<{ success: boolean; data: CollectionPoint; message?: string }>(
        API_PATHS.NOTICE_CONFIG(collectionPointId),
        noticeConfig
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to update notice configuration');
      }

      return response.data;
    } catch (error) {
      console.error('Error updating notice configuration:', error);
      throw error;
    }
  },

  getEventPropagation: async (collectionPointId: number): Promise<EventPropagationItem[]> => {
    const body = await apiClient.get<{ success: boolean; data: EventPropagationItem[] }>(
      API_PATHS.EVENT_PROPAGATION(collectionPointId)
    );
    if (!body?.success) throw new Error('Failed to load event propagation');
    return body.data ?? [];
  },

  saveEventPropagation: async (collectionPointId: number, propagations: EventPropagationItem[]): Promise<EventPropagationItem[]> => {
    const body = await apiClient.put<{ success: boolean; data: EventPropagationItem[]; message?: string }>(
      API_PATHS.EVENT_PROPAGATION(collectionPointId),
      { propagations }
    );
    if (!body?.success) throw new Error(body.message || 'Failed to save event propagation');
    return body.data ?? [];
  }
};

// Consent submission interfaces
export interface PrivacyPolicy {
  title: string;
  effectiveDate: string;
  lastUpdated: string;
  version: string;
  complianceText: string;
  documentInfo: string;
  content: string;
}

export interface YourRights {
  title: string;
  effectiveDate: string;
  lastUpdated: string;
  version: string;
  complianceText: string;
  documentInfo: string;
  content: string;
}

export interface ContactDpo {
  title: string;
  effectiveDate: string;
  lastUpdated: string;
  version: string;
  complianceText: string;
  documentInfo: string;
  content: string;
}

export interface ConsentPayload {
  collectionPointIdentifier: string;
  consentDate: string;
  dataPrincipal: {
    identifier: string;
    identifierType: string;
  };
  purposes: Record<string, {
    consented: boolean;
    timestamp: string;
  }>;
  dataElements: Record<string, string>;
  preferences: Record<string, string | string[]>;
  metadata?: {
    ipAddress?: string;
    languageCode?: string;
    userAgent?: string;
    source?: 'WEB' | 'MOBILE' | 'API' | 'BULK_UPLOAD' | 'NOTICE';
    geolocation?: {
      latitude?: number;
      longitude?: number;
      countryCode?: string;
      region?: string;
      city?: string;
    };
  };
  privacyPolicy?: PrivacyPolicy;
  yourRights?: YourRights;
  contactDpo?: ContactDpo;
}

export interface ConsentResponse {
  success: boolean;
  message: string;
  data?: {
    consentHeaderId: number;
    dataPrincipalId: number;
    submittedAt: string;
  };
} 